import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { MaterialType, Unit } from '../types/enums';
import { Site, Location, Vendor } from '../types/interfaces';

interface ReceiptLine {
  id?: number;
  sortOrder?: number;
  lineType: 'ingredient' | 'fee' | 'freight' | string;
  vendorItemCode?: string | null;
  description?: string | null;
  tillyItemName?: string | null;
  materialType?: string | null;
  quantity: number;
  unit?: string | null;
  unitPrice: number;
  extPrice: number;
  mapped?: number;
  notes?: string | null;
  unmapped?: boolean;
}

interface Receipt {
  id: number;
  status: string;
  vendorName?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  poNumber?: string | null;
  siteId?: string | null;
  freightAmount: number;
  taxAmount: number;
  invoiceTotal: number;
  linesSubtotal: number;
  totalMismatch: number;
  notes?: string | null;
  parseWarnings?: string[];
  hasPdf?: number;
  lines: ReceiptLine[];
}

interface DraftSummary {
  id: number;
  status: string;
  vendorName?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  invoiceTotal: number;
  lineCount: number;
  createdAt?: string | null;
}

interface ItemRow {
  name: string;
  type: string;
  enabled: number;
}

interface Props {
  vendors: Vendor[];
  sites: Site[];
  items: ItemRow[];
  onPosted: () => Promise<void>;
}

const money = (n: number | string | null | undefined) => {
  const v = Number(n || 0);
  return v.toFixed(2);
};

const ReceiveInvoiceUpload: React.FC<Props> = ({ vendors, sites, items, onPosted }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);

  const token = () => localStorage.getItem('token') || '';

  const refreshDrafts = async () => {
    setDraftsLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts?status=draft`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      setDrafts(
        (body.receipts || []).map((r: any) => ({
          id: r.id,
          status: r.status,
          vendorName: r.vendorName,
          invoiceNumber: r.invoiceNumber,
          invoiceDate: r.invoiceDate,
          invoiceTotal: Number(r.invoiceTotal || 0),
          lineCount: Number(r.lineCount || 0),
          createdAt: r.createdAt,
        }))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to load receiving drafts');
    } finally {
      setDraftsLoading(false);
    }
  };

  useEffect(() => {
    void refreshDrafts();
  }, []);

  useEffect(() => {
    if (!receipt?.siteId) {
      setLocations([]);
      setLocationId('');
      return;
    }
    (async () => {
      try {
        const firstIngredient = (receipt.lines || []).find(
          (l) => l.lineType === 'ingredient' && (l.tillyItemName || l.description)
        );
        const itemName = firstIngredient?.tillyItemName || '';
        const itemType = firstIngredient?.materialType || 'Other';
        const [locsRes, resolveRes] = await Promise.all([
          apiFetch(
            `${API_BASE_URL}/api/locations?siteId=${encodeURIComponent(receipt.siteId!)}`,
            { headers: { Authorization: `Bearer ${token()}` } }
          ),
          apiFetch(
            `${API_BASE_URL}/api/storage-defaults/resolve?siteId=${encodeURIComponent(receipt.siteId!)}&itemName=${encodeURIComponent(itemName)}&itemType=${encodeURIComponent(String(itemType))}`,
            { headers: { Authorization: `Bearer ${token()}` } }
          ),
        ]);
        if (!locsRes.ok) throw new Error(await locsRes.text());
        const data: Location[] = await locsRes.json();
        // Prefer receiving locations (excludes brewery coolers for ingredients)
        let resolveBody: any = {};
        if (resolveRes.ok) resolveBody = await resolveRes.json();
        const receiving: Location[] = Array.isArray(resolveBody.receivingLocations)
          ? resolveBody.receivingLocations
          : data.filter((l) => !/brewery cooler/i.test(l.name));
        setLocations(receiving.length ? receiving : data);
        const pref =
          resolveBody.locationId != null
            ? String(resolveBody.locationId)
            : receiving[0]
              ? String(receiving[0].locationId)
              : data[0]
                ? String(data[0].locationId)
                : '';
        setLocationId(pref);
      } catch (err: any) {
        setError(err.message || 'Failed to load locations');
      }
    })();
    // Prefill once per draft receipt / site (not on every line edit).
  }, [receipt?.id, receipt?.siteId]);

  const computedCheck = useMemo(() => {
    if (!receipt) return null;
    const sub = receipt.lines
      .filter((l) => l.lineType === 'ingredient' || l.lineType === 'fee')
      .reduce((s, l) => s + Number(l.extPrice || 0), 0);
    const freight = Number(receipt.freightAmount || 0);
    const tax = Number(receipt.taxAmount || 0);
    const total = Number(receipt.invoiceTotal || 0);
    const sum = Math.round((sub + freight + tax) * 100) / 100;
    const mismatch = Math.round((sum - total) * 100) / 100;
    return { sub, freight, tax, total, sum, mismatch };
  }, [receipt]);

  const unmappedCount = receipt
    ? receipt.lines.filter((l) => l.lineType === 'ingredient' && !l.tillyItemName).length
    : 0;

  /** Unmapped ingredients first so manual mapping is obvious; fees/freight after. */
  const displayLines = useMemo(() => {
    if (!receipt) return [];
    return receipt.lines
      .map((line, index) => ({ line, index }))
      .sort((a, b) => {
        const aUnmapped = a.line.lineType === 'ingredient' && !a.line.tillyItemName ? 0 : 1;
        const bUnmapped = b.line.lineType === 'ingredient' && !b.line.tillyItemName ? 0 : 1;
        if (aUnmapped !== bUnmapped) return aUnmapped - bUnmapped;
        return a.index - b.index;
      });
  }, [receipt]);

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const form = new FormData();
      form.append('pdf', file);
      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/parse`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      setReceipt(body.receipt);
      setShowReview(true);
      setInfo(`Draft #${body.receipt.id} saved — review before posting to inventory.`);
      await refreshDrafts();
    } catch (err: any) {
      setError(err.message || 'Failed to parse invoice PDF');
    } finally {
      setBusy(false);
    }
  };

  const openDraft = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      setReceipt(body);
      setShowReview(true);
      setInfo(`Opened draft #${id} — review and map items, then save or post.`);
    } catch (err: any) {
      setError(err.message || 'Failed to open draft');
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async (id: number) => {
    const ok = window.confirm(
      `Delete receiving draft #${id}? This removes the draft only — inventory is not changed.`
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      if (receipt?.id === id) {
        setReceipt(null);
        setShowReview(false);
      }
      setInfo(`Draft #${id} deleted. Inventory was not changed.`);
      await refreshDrafts();
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setBusy(false);
    }
  };

  const updateLine = (index: number, patch: Partial<ReceiptLine>) => {
    if (!receipt) return;
    const lines = receipt.lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
    setReceipt({ ...receipt, lines });
  };

  const updateHeader = (patch: Partial<Receipt>) => {
    if (!receipt) return;
    setReceipt({ ...receipt, ...patch });
  };

  const saveDraft = async () => {
    if (!receipt) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/${receipt.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorName: receipt.vendorName,
          invoiceNumber: receipt.invoiceNumber,
          invoiceDate: receipt.invoiceDate,
          poNumber: receipt.poNumber,
          siteId: receipt.siteId,
          freightAmount: receipt.freightAmount,
          taxAmount: receipt.taxAmount,
          invoiceTotal: receipt.invoiceTotal,
          notes: receipt.notes,
          lines: receipt.lines.map((l) => ({
            ...l,
            mapped: l.lineType === 'ingredient' ? (l.tillyItemName ? 1 : 0) : 1,
          })),
          saveMappings: true,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      setReceipt(body);
      setInfo(`Draft #${body.id} saved. Vendor item mappings remembered for next upload.`);
      await refreshDrafts();
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setBusy(false);
    }
  };

  const postReceipt = async () => {
    if (!receipt) return;
    if (!locationId) {
      setError('Choose a storage location before posting to inventory');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const patchRes = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/${receipt.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorName: receipt.vendorName,
          invoiceNumber: receipt.invoiceNumber,
          invoiceDate: receipt.invoiceDate,
          poNumber: receipt.poNumber,
          siteId: receipt.siteId,
          freightAmount: receipt.freightAmount,
          taxAmount: receipt.taxAmount,
          invoiceTotal: receipt.invoiceTotal,
          notes: receipt.notes,
          lines: receipt.lines.map((l) => ({
            ...l,
            mapped: l.lineType === 'ingredient' ? (l.tillyItemName ? 1 : 0) : 1,
          })),
          saveMappings: true,
        }),
      });
      const patched = await patchRes.json().catch(() => ({}));
      if (!patchRes.ok) throw new Error(patched.error || patchRes.statusText);

      const res = await apiFetch(`${API_BASE_URL}/api/inventory-receipts/${receipt.id}/post`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          locationId: Number(locationId) || locationId,
          allowUnmapped: false,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || res.statusText);
      setReceipt(body.receipt);
      setShowReview(false);
      setInfo(`Receipt #${body.receipt.id} posted to inventory.`);
      await refreshDrafts();
      await onPosted();
    } catch (err: any) {
      setError(err.message || 'Failed to post receipt');
    } finally {
      setBusy(false);
    }
  };

  /** From drafts list: open review first so location/mappings can be confirmed, then post. */
  const postDraftFromList = async (id: number) => {
    await openDraft(id);
    setInfo(`Draft #${id} opened — choose a location and click Post to inventory.`);
  };

  const lineTypeLabel = (t: string) => {
    if (t === 'fee') return 'Fee';
    if (t === 'freight') return 'Freight';
    return 'Item';
  };

  return (
    <div
      style={{
        marginBottom: '24px',
        padding: '16px',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        background: '#fafafa',
      }}
    >
      <h2 style={{ margin: '0 0 8px', fontSize: '18px', color: '#333' }}>Vendor Invoice PDF</h2>
      <p style={{ margin: '0 0 12px', color: '#666', fontSize: '14px' }}>
        Upload a vendor invoice to create a <strong>draft</strong> receipt. Review and map items, then
        save or post — inventory is never updated until you confirm.
      </p>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label
          style={{
            display: 'inline-block',
            background: busy ? '#999' : '#2196F3',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: '4px',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: '14px',
          }}
        >
          {busy ? 'Working…' : 'Upload Invoice PDF'}
          <input
            type="file"
            accept="application/pdf,.pdf"
            disabled={busy}
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              e.target.value = '';
              void handleUpload(f);
            }}
          />
        </label>
        {receipt?.hasPdf ? (
          <a
            href={`${API_BASE_URL}/api/inventory-receipts/${receipt.id}/pdf`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              // attach auth via fetch blob would be nicer; open with token query not available —
              // use fetch download instead
              e.preventDefault();
              void (async () => {
                const res = await apiFetch(
                  `${API_BASE_URL}/api/inventory-receipts/${receipt.id}/pdf`,
                  { headers: { Authorization: `Bearer ${token()}` } }
                );
                if (!res.ok) return;
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
              })();
            }}
            style={{ fontSize: '14px' }}
          >
            View attached PDF
          </a>
        ) : null}
      </div>

      <div style={{ marginTop: 18 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#333' }}>Receiving Drafts</h3>
        <p style={{ margin: '0 0 10px', color: '#666', fontSize: 13 }}>
          Upload auto-saves a draft. Open to resume editing; delete removes the draft only (never
          inventory). Posted receipts leave this list.
        </p>
        {draftsLoading && !drafts.length ? (
          <div style={{ color: '#888', fontSize: 13 }}>Loading drafts…</div>
        ) : drafts.length === 0 ? (
          <div style={{ color: '#888', fontSize: 13 }}>No open receiving drafts.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555' }}>
                  <th style={{ padding: '6px 4px' }}>Draft #</th>
                  <th style={{ padding: '6px 4px' }}>Vendor</th>
                  <th style={{ padding: '6px 4px' }}>Date</th>
                  <th style={{ padding: '6px 4px' }}>Lines</th>
                  <th style={{ padding: '6px 4px' }}>Total</th>
                  <th style={{ padding: '6px 4px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px 4px', fontWeight: 600 }}>#{d.id}</td>
                    <td style={{ padding: '8px 4px' }}>
                      {d.vendorName || '—'}
                      {d.invoiceNumber ? (
                        <div style={{ color: '#888', fontSize: 11 }}>{d.invoiceNumber}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '8px 4px' }}>{d.invoiceDate || '—'}</td>
                    <td style={{ padding: '8px 4px' }}>{d.lineCount}</td>
                    <td style={{ padding: '8px 4px' }}>${money(d.invoiceTotal)}</td>
                    <td style={{ padding: '8px 4px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void openDraft(d.id)}
                          style={{
                            background: '#2196F3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            padding: '6px 10px',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void postDraftFromList(d.id)}
                          style={{
                            background: '#28A745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            padding: '6px 10px',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Post
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void deleteDraft(d.id)}
                          style={{
                            background: '#fff',
                            color: '#a33',
                            border: '1px solid #e0a0a0',
                            borderRadius: 4,
                            padding: '6px 10px',
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: '12px',
            color: '#F86752',
            background: '#ffe6e6',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {error}
        </div>
      )}
      {info && (
        <div
          style={{
            marginTop: '12px',
            color: '#28A745',
            background: '#e6ffe6',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {info}
        </div>
      )}

      {showReview && receipt && (
        <div style={{ marginTop: '16px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '10px',
              marginBottom: '12px',
            }}
          >
            <label style={{ fontSize: '13px' }}>
              Vendor
              <select
                value={receipt.vendorName || ''}
                onChange={(e) => updateHeader({ vendorName: e.target.value })}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              >
                <option value="">—</option>
                {vendors.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '13px' }}>
              Invoice #
              <input
                value={receipt.invoiceNumber || ''}
                onChange={(e) => updateHeader({ invoiceNumber: e.target.value })}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              />
            </label>
            <label style={{ fontSize: '13px' }}>
              Invoice date
              <input
                type="date"
                value={receipt.invoiceDate || ''}
                onChange={(e) => updateHeader({ invoiceDate: e.target.value })}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              />
            </label>
            <label style={{ fontSize: '13px' }}>
              PO / Ref
              <input
                value={receipt.poNumber || ''}
                onChange={(e) => updateHeader({ poNumber: e.target.value })}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              />
            </label>
            <label style={{ fontSize: '13px' }}>
              Site
              <select
                value={receipt.siteId || ''}
                onChange={(e) => updateHeader({ siteId: e.target.value })}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              >
                <option value="">—</option>
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteId}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '13px' }}>
              Receive location
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 6 }}
              >
                <option value="">—</option>
                {locations.map((l) => (
                  <option key={l.locationId} value={String(l.locationId)}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {(receipt.parseWarnings || []).length > 0 && (
            <ul style={{ color: '#b36b00', fontSize: '13px', marginBottom: '10px' }}>
              {receipt.parseWarnings!.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          {unmappedCount > 0 && (
            <div
              style={{
                background: '#fff3cd',
                color: '#856404',
                padding: '8px',
                borderRadius: 4,
                marginBottom: 10,
                fontSize: 13,
              }}
            >
              <strong>{unmappedCount} unmatched line(s)</strong> need a Tilly item (highlighted at
              top). Pick or type an item — it will be remembered for this vendor. Lines are never
              dropped; map them here before posting.
            </div>
          )}

          {receipt.lines.filter((l) => l.lineType === 'ingredient').length === 0 && (
            <div
              style={{
                background: '#ffe6e6',
                color: '#a33',
                padding: '8px',
                borderRadius: 4,
                marginBottom: 10,
                fontSize: 13,
              }}
            >
              No grain/ingredient lines were detected (only shipping/fees). Re-check the PDF or add
              mappings if lines appear below as fees.
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#eee', textAlign: 'left' }}>
                  <th style={{ padding: 6 }}>Type</th>
                  <th style={{ padding: 6 }}>Vendor code</th>
                  <th style={{ padding: 6 }}>Description</th>
                  <th style={{ padding: 6 }}>Tilly item</th>
                  <th style={{ padding: 6 }}>Qty</th>
                  <th style={{ padding: 6 }}>Unit</th>
                  <th style={{ padding: 6 }}>Unit $</th>
                  <th style={{ padding: 6 }}>Ext $</th>
                </tr>
              </thead>
              <tbody>
                {displayLines.map(({ line, index: idx }) => {
                  const needsMap = line.lineType === 'ingredient' && !line.tillyItemName;
                  return (
                    <tr
                      key={idx}
                      style={{
                        background: needsMap ? '#fff8e1' : line.lineType === 'freight' ? '#f0f7ff' : '#fff',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      <td style={{ padding: 6 }}>
                        {lineTypeLabel(line.lineType)}
                        {needsMap ? (
                          <div style={{ color: '#b36b00', fontSize: 11 }}>Unmatched</div>
                        ) : null}
                      </td>
                      <td style={{ padding: 6 }}>{line.vendorItemCode || '—'}</td>
                      <td style={{ padding: 6 }}>
                        <div>{line.description}</div>
                        {line.notes && (
                          <div style={{ color: '#888', fontSize: 11 }}>{line.notes}</div>
                        )}
                      </td>
                      <td style={{ padding: 6, minWidth: 160 }}>
                        {line.lineType === 'ingredient' ? (
                          <input
                            list={`tilly-items-${idx}`}
                            value={line.tillyItemName || ''}
                            onChange={(e) => {
                              const name = e.target.value;
                              const found = items.find((it) => it.name === name);
                              updateLine(idx, {
                                tillyItemName: name,
                                materialType: found?.type || line.materialType || MaterialType.Grain,
                                mapped: name ? 1 : 0,
                              });
                            }}
                            placeholder="Pick / type item"
                            style={{
                              width: '100%',
                              padding: 4,
                              border: needsMap ? '1px solid #e0a800' : undefined,
                            }}
                          />
                        ) : (
                          <span style={{ color: '#888' }}>—</span>
                        )}
                        <datalist id={`tilly-items-${idx}`}>
                          {items.map((it) => (
                            <option key={it.name} value={it.name} />
                          ))}
                        </datalist>
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          step="0.01"
                          value={line.quantity}
                          onChange={(e) => {
                            const quantity = Number(e.target.value);
                            const extPrice =
                              Math.round(quantity * Number(line.unitPrice || 0) * 100) / 100;
                            updateLine(idx, { quantity, extPrice });
                          }}
                          style={{ width: 70, padding: 4 }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>
                        <select
                          value={line.unit || Unit.Count}
                          onChange={(e) => updateLine(idx, { unit: e.target.value })}
                          style={{ padding: 4 }}
                        >
                          {Object.values(Unit).map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: 6 }}>
                        <input
                          type="number"
                          step="0.01"
                          value={line.unitPrice}
                          onChange={(e) => {
                            const unitPrice = Number(e.target.value);
                            const extPrice =
                              Math.round(Number(line.quantity || 0) * unitPrice * 100) / 100;
                            updateLine(idx, { unitPrice, extPrice });
                          }}
                          style={{ width: 80, padding: 4 }}
                        />
                      </td>
                      <td style={{ padding: 6 }}>{money(line.extPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {computedCheck && (
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 4,
                background: Math.abs(computedCheck.mismatch) > 0.02 ? '#ffe6e6' : '#e8f5e9',
                color: Math.abs(computedCheck.mismatch) > 0.02 ? '#a33' : '#2e7d32',
                fontSize: 14,
              }}
            >
              Lines {money(computedCheck.sub)} + freight {money(computedCheck.freight)} + tax{' '}
              {money(computedCheck.tax)} = {money(computedCheck.sum)}
              {' vs '}
              invoice total {money(computedCheck.total)}
              {Math.abs(computedCheck.mismatch) > 0.02
                ? ` — mismatch ${money(computedCheck.mismatch)}`
                : ' — OK'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={busy || receipt.status !== 'draft'}
              onClick={() => void saveDraft()}
              style={{
                background: '#555',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '10px 16px',
                cursor: 'pointer',
              }}
            >
              Save draft
            </button>
            <button
              type="button"
              disabled={busy || receipt.status !== 'draft' || unmappedCount > 0}
              onClick={() => void postReceipt()}
              style={{
                background: unmappedCount > 0 ? '#aaa' : '#28A745',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '10px 16px',
                cursor: unmappedCount > 0 ? 'not-allowed' : 'pointer',
              }}
              title={unmappedCount > 0 ? 'Map all ingredient lines first' : 'Post to inventory'}
            >
              Post to inventory
            </button>
            <button
              type="button"
              onClick={() => {
                const id = receipt?.id;
                setShowReview(false);
                setReceipt(null);
                setInfo(
                  id
                    ? `Draft #${id} kept in Receiving Drafts — open it anytime to finish posting.`
                    : null
                );
                void refreshDrafts();
              }}
              style={{
                background: '#fff',
                color: '#333',
                border: '1px solid #888',
                borderRadius: 4,
                padding: '10px 16px',
                cursor: 'pointer',
              }}
              title="Hide the review panel. The draft stays saved in Receiving Drafts."
            >
              Close review
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiveInvoiceUpload;
